import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Gavel, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatCurrency, currencySymbol } from "@/lib/currency";

/**
 * CloseBopDialog — per-line award picker for BOP RFQs.
 *
 * Privacy model: bidders show up only by anonymous labels ("B1", "B2", ...).
 * The matrix endpoint never reveals identities. Identities are resolved
 * server-side once the creator commits awards.
 */
export const CloseBopDialog = ({ auctionId, hasBids, onChange }) => {
  const [open, setOpen] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [awards, setAwards] = useState({}); // {item_number: label}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/auctions/${auctionId}/bop-award-matrix`);
      setMatrix(data);
      // Default: prefill with the per-line lowest label
      const init = {};
      for (const li of data.line_items || []) {
        const best = data.line_lowest?.[li.item_number]?.label;
        if (best) init[li.item_number] = best;
      }
      setAwards(init);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load award matrix");
    } finally { setLoading(false); }
  }, [auctionId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const allAwarded = useMemo(() => {
    if (!matrix) return false;
    return (matrix.line_items || []).every((li) => awards[li.item_number]);
  }, [matrix, awards]);

  const projectedTotal = useMemo(() => {
    if (!matrix) return 0;
    let t = 0;
    for (const li of matrix.line_items || []) {
      const lbl = awards[li.item_number];
      if (!lbl) continue;
      const cell = matrix.cells?.[li.item_number]?.[lbl];
      if (cell) t += cell.line_total;
    }
    return Math.round(t * 100) / 100;
  }, [matrix, awards]);

  const submit = async (mode) => {
    setBusy(true);
    try {
      const payload = mode === "no-winner"
        ? { declare_winner: false }
        : { declare_winner: true, line_awards: awards };
      await api.post(`/auctions/${auctionId}/close`, payload);
      toast.success(mode === "no-winner" ? "Closed without awards" : "Closed with per-line awards");
      setOpen(false);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const sym = matrix ? currencySymbol(matrix.currency) : "";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full rounded-none border-black h-11 btn-sharp"
          data-testid="open-close-bop-dialog"
        >
          <Gavel size={14} weight="bold" className="mr-2" /> Close & Award
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-5xl" data-testid="close-bop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest">
            Close BOP RFQ — Award per Line
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Pick one bidder per line item. Bidders are shown under anonymous labels (<strong>B1, B2, …</strong>) — their real
          identities are resolved server-side only after you commit. The lowest unit price per line is pre-selected.
        </p>

        {loading ? (
          <div className="py-8 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading award matrix...</div>
        ) : !matrix ? null : matrix.bidder_labels.length === 0 ? (
          <div className="border border-dashed border-black/15 p-6 text-center text-sm text-neutral-600">
            No bids have been placed yet — there is nothing to award.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto border border-black/10 mt-2">
              <table className="w-full text-sm" data-testid="award-matrix-table">
                <thead className="bg-[#F8F9FA] border-b border-black/10">
                  <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                    <th className="text-left p-3">Item</th>
                    <th className="text-right p-3">Qty</th>
                    {matrix.bidder_labels.map((lbl) => (
                      <th key={lbl} className="text-right p-3" data-testid={`matrix-col-${lbl}`}>{lbl}</th>
                    ))}
                    <th className="text-center p-3">Award To</th>
                  </tr>
                </thead>
                <tbody>
                  {(matrix.line_items || []).map((li) => {
                    const row = matrix.cells?.[li.item_number] || {};
                    const lowest = matrix.line_lowest?.[li.item_number];
                    return (
                      <tr key={li.item_number} className="border-b border-black/5" data-testid={`award-row-${li.item_number}`}>
                        <td className="p-3">
                          <div className="font-mono-plex font-semibold text-sm">{li.item_number}</div>
                          <div className="text-xs text-neutral-600">{li.item_desc}</div>
                        </td>
                        <td className="p-3 text-right font-mono-plex text-xs">{Number(li.forecast_qty).toLocaleString()} {li.uom || ""}</td>
                        {matrix.bidder_labels.map((lbl) => {
                          const cell = row[lbl];
                          const isLowest = lowest?.label === lbl;
                          return (
                            <td key={lbl} className={`p-3 text-right font-mono-plex text-xs ${isLowest ? "klein-blue font-semibold" : ""}`}>
                              {cell ? (
                                <>
                                  {sym}{cell.unit_price}
                                  <div className="text-[10px] text-neutral-500">{sym}{cell.line_total.toLocaleString()}</div>
                                </>
                              ) : <span className="text-neutral-400">—</span>}
                            </td>
                          );
                        })}
                        <td className="p-3 text-center">
                          <select
                            value={awards[li.item_number] || ""}
                            onChange={(e) => setAwards({ ...awards, [li.item_number]: e.target.value })}
                            className="rounded-none border border-black/30 h-9 px-2 text-sm font-mono-plex"
                            data-testid={`award-select-${li.item_number}`}
                          >
                            <option value="">—</option>
                            {matrix.bidder_labels
                              .filter((lbl) => row[lbl])
                              .map((lbl) => (
                                <option key={lbl} value={lbl}>{lbl}</option>
                              ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-black bg-[#F8F9FA] font-semibold">
                    <td colSpan={2} className="p-3 text-right text-[10px] tracking-[0.25em] uppercase text-neutral-500">Total per bidder</td>
                    {matrix.bidder_labels.map((lbl) => (
                      <td key={lbl} className="p-3 text-right font-mono-plex text-xs">
                        {sym}{(matrix.totals[lbl] ?? 0).toLocaleString()}
                      </td>
                    ))}
                    <td className="p-3 text-center text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                      Projected: <span className="klein-blue font-mono-plex">{formatCurrency(projectedTotal, matrix.currency)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-neutral-500 leading-relaxed">
              You can split the RFQ across vendors — e.g. award item A to <strong>B1</strong> and item B to <strong>B2</strong>.
              Once committed, every bidder will be emailed with the items they won (or a polite no-win note).
            </p>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          {hasBids && matrix && matrix.bidder_labels.length > 0 ? (
            <>
              <Button
                onClick={() => submit("no-winner")}
                disabled={busy}
                variant="outline"
                className="rounded-none border-black h-11 btn-sharp"
                data-testid="close-bop-no-winner"
              >
                {busy ? "Closing..." : "Close without awards"}
              </Button>
              <Button
                onClick={() => submit("commit")}
                disabled={busy || !allAwarded}
                className="rounded-none bg-[#22D3EE] text-[#0B1929] font-semibold h-11 btn-sharp"
                data-testid="close-bop-commit"
              >
                <Trophy size={14} weight="bold" className="mr-2" />
                {busy ? "Awarding..." : "Commit per-line awards & close"}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => submit("no-winner")}
              disabled={busy}
              className="rounded-none bg-[#0B1929] text-white h-11 btn-sharp"
              data-testid="close-bop-no-winner"
            >
              {busy ? "Closing..." : "Close auction"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
